import type { KeyValueStorage, PersistenceAwareStorage } from '@dcs/platform/ports';
import { createWebStorage } from '@dcs/platform/web';
import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';
import {
  DARK_SCHEME_QUERY,
  THEME_COLORS,
  THEME_STORAGE_KEY,
  parseThemePreference,
  resolveTheme,
  type ResolvedTheme,
  type ThemePreference,
} from './theme';

/**
 * Theme state. Changes only when the user picks a theme or the system scheme flips (a media
 * query event), never per frame.
 */
export interface ThemeState {
  /** What the user picked; `system` follows `prefers-color-scheme`. */
  readonly preference: ThemePreference;
  /** The side the page is drawn in right now. */
  readonly resolved: ResolvedTheme;
  /** `false` when the choice lives in memory only (storage blocked or a write failed). */
  readonly persistent: boolean;
  setPreference(preference: ThemePreference): void;
}

export type ThemeStore = StoreApi<ThemeState>;

/** The part of `MediaQueryList` the store listens to. */
export interface SchemeQuery {
  readonly matches: boolean;
  addEventListener(type: 'change', listener: (event: { matches: boolean }) => void): void;
}

/** The part of `Document` the store writes to. */
export interface ThemeDocument {
  readonly documentElement: {
    setAttribute(name: string, value: string): void;
    removeAttribute(name: string): void;
  };
  querySelector(selector: string): { setAttribute(name: string, value: string): void } | null;
}

/**
 * The part of `Window` that reports `localStorage` changes made by another tab (the `storage`
 * event fires in every other document of the origin, never in the one that wrote).
 */
export interface StorageEvents {
  addEventListener(
    type: 'storage',
    listener: (event: { readonly key: string | null; readonly newValue: string | null }) => void,
  ): void;
}

export interface ThemeStoreOptions {
  /** Where `dcs.theme` lives (`createWebStorage` in the app). A throwing storage turns to memory. */
  readonly storage: KeyValueStorage | PersistenceAwareStorage;
  /** `<html>` gets `data-theme`, `<meta name="theme-color">` its colour. `null`: no DOM (tests). */
  readonly document: ThemeDocument | null;
  /** `(prefers-color-scheme: dark)`; `null` when the browser cannot tell (then light). */
  readonly schemeQuery: SchemeQuery | null;
  /** `window`: a change of `dcs.theme` in another tab is applied here. Omitted / `null`: none. */
  readonly storageEvents?: StorageEvents | null | undefined;
}

function storageIsPersistent(storage: KeyValueStorage | PersistenceAwareStorage): boolean {
  return 'persistent' in storage ? storage.persistent : true;
}

/** Puts a resolved theme on the page: `data-theme` only for a theme picked by hand. */
export function applyTheme(
  doc: ThemeDocument,
  preference: ThemePreference,
  resolved: ResolvedTheme,
): void {
  if (preference === 'system') doc.documentElement.removeAttribute('data-theme');
  else doc.documentElement.setAttribute('data-theme', preference);
  doc.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLORS[resolved]);
}

/**
 * Vanilla Zustand store over the storage port. Reads `dcs.theme` once, applies it to the page at
 * once (so `<meta name="theme-color">` follows what `theme-init.js` already set) and on every
 * change. Never throws: unknown values read as `system`, a storage that throws keeps the choice in
 * memory for the session with `persistent: false`.
 */
export function createThemeStore(options: ThemeStoreOptions): ThemeStore {
  const { storage, document: doc, schemeQuery, storageEvents } = options;
  let memoryOnly = false;

  function load(): ThemePreference {
    try {
      return parseThemePreference(storage.get(THEME_STORAGE_KEY));
    } catch {
      memoryOnly = true;
      return 'system';
    }
  }

  function write(preference: ThemePreference): void {
    if (memoryOnly) return;
    try {
      storage.set(THEME_STORAGE_KEY, preference);
    } catch {
      memoryOnly = true;
    }
  }

  const persistent = () => !memoryOnly && storageIsPersistent(storage);
  const systemDark = () => schemeQuery?.matches ?? false;

  const preference = load();
  const store = createStore<ThemeState>()((set) => ({
    preference,
    resolved: resolveTheme(preference, systemDark()),
    persistent: persistent(),
    setPreference(next) {
      write(next);
      set({
        preference: next,
        resolved: resolveTheme(next, systemDark()),
        persistent: persistent(),
      });
    },
  }));

  schemeQuery?.addEventListener('change', ({ matches }) => {
    const { preference: current, resolved } = store.getState();
    const next = resolveTheme(current, matches);
    if (next !== resolved) store.setState({ resolved: next });
  });

  // Another tab changed `dcs.theme` (or cleared the storage): follow it without writing it back.
  storageEvents?.addEventListener('storage', ({ key, newValue }) => {
    if (key !== null && key !== THEME_STORAGE_KEY) return;
    const next = parseThemePreference(newValue);
    if (next === store.getState().preference) return;
    store.setState({ preference: next, resolved: resolveTheme(next, systemDark()) });
  });

  if (doc !== null) {
    const { preference: p, resolved: r } = store.getState();
    applyTheme(doc, p, r);
    store.subscribe((state, prev) => {
      if (state.preference !== prev.preference || state.resolved !== prev.resolved) {
        applyTheme(doc, state.preference, state.resolved);
      }
    });
  }

  return store;
}

function browserSchemeQuery(): SchemeQuery | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(DARK_SCHEME_QUERY) : null;
}

let shared: ThemeStore | null = null;

/**
 * The app-wide theme store over `localStorage` (in-memory fallback when blocked), bound to this
 * document. `main.tsx` creates it before the first render; the ThemeSwitch of `/toi` and of the
 * drive settings share it.
 */
export function getThemeStore(): ThemeStore {
  shared ??= createThemeStore({
    storage: createWebStorage(),
    document,
    schemeQuery: browserSchemeQuery(),
    storageEvents: window,
  });
  return shared;
}

/** React hook over the theme store; pass a selector that returns a primitive. */
export function useTheme<T>(
  selector: (state: ThemeState) => T,
  store: ThemeStore = getThemeStore(),
): T {
  return useStore(store, selector);
}

export const selectPreference = (state: ThemeState): ThemePreference => state.preference;
export const selectResolved = (state: ThemeState): ResolvedTheme => state.resolved;
export const selectPersistent = (state: ThemeState): boolean => state.persistent;
